import { useState } from "react";
import { BookmarkPlus, Check, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/* Save a searched flight route straight into My Travel Hub — pick an existing trip
   or create a new one; adds flight segment(s) (and the return leg on round trips). */
export default function SaveRouteToTrip({ from, to, depart, ret, type }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [trips, setTrips] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  if (!user || !from?.trim() || !to?.trim()) return null;

  const openPicker = async () => {
    setOpen(true);
    if (trips === null) {
      try { const r = await api.get("/trips"); setTrips(r.data.items || r.data || []); }
      catch { setTrips([]); }
    }
  };

  const addSegments = async (tid) => {
    const t = (await api.get(`/trips/${tid}`)).data;
    const segs = [...(t.segments || []),
      { mode: "flight", from_place: from.trim(), to_place: to.trim(), date: depart || "" }];
    if (type === "round" && ret) {
      segs.push({ mode: "flight", from_place: to.trim(), to_place: from.trim(), date: ret });
    }
    await api.put(`/trips/${tid}/segments`, { segments: segs });
  };

  const save = async (tid) => {
    setBusy(true);
    try {
      let id = tid;
      if (!id) {
        const r = await api.post("/trips", {
          title: `${from.trim()} → ${to.trim()}`,
          start_date: depart || null, end_date: (type === "round" && ret) || depart || null,
          stops: [from.trim(), to.trim()],
        });
        id = r.data.id;
      }
      await addSegments(id);
      setSaved(true); setOpen(false);
      toast.success(tid ? "Route added to your trip" : "New trip created with this route", {
        action: { label: "Open trip", onClick: () => window.location.assign(`/trips/${id}`) },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the route");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2.5">
      {saved ? (
        <span className="inline-flex items-center gap-1.5 text-emerald-700 text-xs font-800" data-testid="route-saved-badge">
          <Check className="w-4 h-4" /> Saved to My Travel Hub
        </span>
      ) : (
        <button type="button" onClick={openPicker} data-testid="save-route-to-trip"
          className="inline-flex items-center gap-1.5 text-xs font-800 text-blue-800 border border-blue-300 bg-white px-3 py-1.5 rounded-full hover:border-blue-600 transition-colors">
          <BookmarkPlus className="w-3.5 h-3.5" /> Save this route to My Travel Hub
        </button>
      )}
      {open && !saved && (
        <div className="mt-2 border border-zinc-200 bg-white p-3" data-testid="save-route-picker">
          <div className="text-xs font-700 text-zinc-600 mb-2">
            Save {from.trim()} → {to.trim()}{depart ? ` (${depart})` : ""} into:
          </div>
          {trips === null ? <Loader2 className="w-4 h-4 animate-spin" /> : (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => save(null)} disabled={busy} data-testid="save-route-new-trip"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-700 text-white text-xs font-800 hover:bg-blue-800 disabled:opacity-50">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} New trip
              </button>
              {trips.slice(0, 8).map((t) => (
                <button key={t.id} onClick={() => save(t.id)} disabled={busy} data-testid={`save-route-trip-${t.id}`}
                  className="px-3 py-1.5 border border-blue-600 text-blue-700 text-xs font-700 hover:bg-blue-50 disabled:opacity-50">
                  {t.title}
                </button>
              ))}
              <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs text-muted-foreground">Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
